import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import api from '../../services/api';
import type {
  AccountTrendFocus,
  AccountTrendGranularity,
  AccountTrendMetric,
  AccountTrendResponse
} from '../../types/accountTrend';
import {
  focusAnchorFromBucket,
  getTodayAnchor,
  normalizeFocusAnchor,
  shiftFocusAnchor
} from '../../utils/accountDashboardFocus';

const METRIC_STORAGE_PREFIX = 'organizationTrendMetric:';

function readStoredMetric(organizationId: string): AccountTrendMetric | null {
  try {
    const raw = sessionStorage.getItem(`${METRIC_STORAGE_PREFIX}${organizationId}`);
    if (raw === 'hours' || raw === 'internal_cost' || raw === 'billable') {
      return raw;
    }
  } catch {
    // ignore
  }
  return null;
}

function writeStoredMetric(organizationId: string, metric: AccountTrendMetric): void {
  try {
    sessionStorage.setItem(`${METRIC_STORAGE_PREFIX}${organizationId}`, metric);
  } catch {
    // ignore
  }
}

export interface OrganizationDashboardContextValue {
  organizationId: string;
  accessGranted: boolean | null;
  metric: AccountTrendMetric;
  granularity: AccountTrendGranularity;
  anchor: string;
  focus: AccountTrendFocus | null;
  focusWindow: { from: string; to: string; label: string } | null;
  trendData: AccountTrendResponse | null;
  trendLoading: boolean;
  trendRefreshing: boolean;
  trendError: string | null;
  currencyCode: string;
  organizationTimezone: string;
  currencyWarning: boolean;
  setMetric: (metric: AccountTrendMetric) => void;
  setGranularity: (granularity: AccountTrendGranularity) => void;
  goPrev: () => void;
  goNext: () => void;
  setFocusFromBucket: (bucket: { key: string; from: string; to: string }) => void;
  refreshAccess: () => Promise<void>;
}

const OrganizationDashboardContext = createContext<OrganizationDashboardContextValue | null>(null);

export function useOrganizationDashboard(): OrganizationDashboardContextValue {
  const ctx = useContext(OrganizationDashboardContext);
  if (!ctx) {
    throw new Error('useOrganizationDashboard must be used within OrganizationDashboardProvider');
  }
  return ctx;
}

export function useOrganizationDashboardOptional(): OrganizationDashboardContextValue | null {
  return useContext(OrganizationDashboardContext);
}

interface OrganizationDashboardProviderProps {
  organizationId: string;
  organizationCurrency: string;
  children: React.ReactNode;
}

export function OrganizationDashboardProvider({
  organizationId,
  organizationCurrency,
  children
}: OrganizationDashboardProviderProps) {
  const [accessGranted, setAccessGranted] = useState<boolean | null>(null);
  const [metric, setMetricState] = useState<AccountTrendMetric>('hours');
  const [granularity, setGranularityState] = useState<AccountTrendGranularity>('month');
  const [anchor, setAnchor] = useState(() => normalizeFocusAnchor(getTodayAnchor(), 'month'));
  const [trendData, setTrendData] = useState<AccountTrendResponse | null>(null);
  const [trendLoading, setTrendLoading] = useState(true);
  const [trendRefreshing, setTrendRefreshing] = useState(false);
  const [trendError, setTrendError] = useState<string | null>(null);
  const trendRequestRef = useRef(0);
  const trendDataRef = useRef<AccountTrendResponse | null>(null);
  trendDataRef.current = trendData;

  const refreshAccess = useCallback(async () => {
    try {
      setTrendError(null);
      const res = await api.getOrganizationBudgetProgress(organizationId);
      if (res.success) {
        setAccessGranted(true);
        const stored = readStoredMetric(organizationId);
        setMetricState(stored ?? 'hours');
        if (!stored) {
          writeStoredMetric(organizationId, 'hours');
        }
      } else {
        setAccessGranted(false);
      }
    } catch (err: unknown) {
      const status =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 403) {
        setAccessGranted(false);
        return;
      }
      setAccessGranted(false);
    }
  }, [organizationId]);

  useEffect(() => {
    setAccessGranted(null);
    setTrendData(null);
    setAnchor(normalizeFocusAnchor(getTodayAnchor(), 'month'));
    refreshAccess();
  }, [organizationId, refreshAccess]);

  const setMetric = useCallback(
    (next: AccountTrendMetric) => {
      setMetricState(next);
      writeStoredMetric(organizationId, next);
    },
    [organizationId]
  );

  const setGranularity = useCallback((next: AccountTrendGranularity) => {
    setGranularityState(next);
    setAnchor(normalizeFocusAnchor(getTodayAnchor(), next));
  }, []);

  const goPrev = useCallback(() => {
    setAnchor((prev) => shiftFocusAnchor(prev, granularity, -1));
  }, [granularity]);

  const goNext = useCallback(() => {
    setAnchor((prev) => shiftFocusAnchor(prev, granularity, 1));
  }, [granularity]);

  const setFocusFromBucket = useCallback(
    (bucket: { key: string; from: string; to: string }) => {
      setAnchor(focusAnchorFromBucket(bucket.from, granularity));
    },
    [granularity]
  );

  const loadTrend = useCallback(async () => {
    if (!accessGranted) {
      setTrendLoading(false);
      setTrendRefreshing(false);
      return;
    }

    const requestId = ++trendRequestRef.current;
    const hasExistingData = trendDataRef.current !== null;

    try {
      if (hasExistingData) {
        setTrendRefreshing(true);
      } else {
        setTrendLoading(true);
      }
      setTrendError(null);

      const res = await api.getOrganizationTrend(organizationId, { metric, granularity, anchor });
      if (requestId !== trendRequestRef.current) {
        return;
      }

      if (res.success) {
        setTrendData(res.data as AccountTrendResponse);
      } else {
        setTrendData(null);
        setTrendError(res.message ?? 'Failed to load organization trend');
      }
    } catch (err: unknown) {
      if (requestId !== trendRequestRef.current) {
        return;
      }
      const status =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 403) {
        setAccessGranted(false);
        setTrendData(null);
        return;
      }
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setTrendData(null);
      setTrendError(message ?? 'Failed to load organization trend');
    } finally {
      if (requestId === trendRequestRef.current) {
        setTrendLoading(false);
        setTrendRefreshing(false);
      }
    }
  }, [accessGranted, organizationId, metric, granularity, anchor]);

  useEffect(() => {
    loadTrend();
  }, [loadTrend]);

  const focus = trendData?.focus ?? null;

  const focusWindow = useMemo(() => {
    if (!focus) return null;
    return {
      from: focus.from,
      to: focus.to,
      label: focus.label
    };
  }, [focus]);

  const currencyCode = trendData?.meta.currencyCode ?? organizationCurrency;
  const organizationTimezone = trendData?.meta.timezone ?? 'UTC';
  const currencyWarning = Boolean(trendData?.meta.currencyWarning);

  const value = useMemo(
    (): OrganizationDashboardContextValue => ({
      organizationId,
      accessGranted,
      metric,
      granularity,
      anchor,
      focus,
      focusWindow,
      trendData,
      trendLoading,
      trendRefreshing,
      trendError,
      currencyCode,
      organizationTimezone,
      currencyWarning,
      setMetric,
      setGranularity,
      goPrev,
      goNext,
      setFocusFromBucket,
      refreshAccess
    }),
    [
      organizationId,
      accessGranted,
      metric,
      granularity,
      anchor,
      focus,
      focusWindow,
      trendData,
      trendLoading,
      trendRefreshing,
      trendError,
      currencyCode,
      organizationTimezone,
      currencyWarning,
      setMetric,
      setGranularity,
      goPrev,
      goNext,
      setFocusFromBucket,
      refreshAccess
    ]
  );

  return (
    <OrganizationDashboardContext.Provider value={value}>
      {children}
    </OrganizationDashboardContext.Provider>
  );
}
